/*
 * @Author: Ryan
 * @Date: 2026-01-08
 * @Description: 视频翻译 Landing 页面入口
 */
import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import { getLanguageNameByCode } from '@/hooks/useTranslationConfig';
import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead from '@/landing/components/SEOHead';
import ExploreToolsSection from '@/landing/components/templates/ExploreToolsSection';
import FAQSection, { FAQItem } from '@/landing/components/templates/FAQSection';
import FeaturesSection, {
  FeatureItem,
} from '@/landing/components/templates/FeaturesSection';
import FooterCTASection from '@/landing/components/templates/FooterCTASection';
import HeroSection from '@/landing/components/templates/HeroSection';
import HowToSection, { StepData } from '@/landing/components/templates/HowToSection';
import LanguageListSection from '@/landing/components/templates/LanguageListSection';
import UseCasesSection, {
  UseCaseItem,
} from '@/landing/components/templates/UseCasesSection';
import VideoTranslationDemos from '@/landing/components/templates/VideoTranslationDemos';
import VsSection from '@/landing/components/templates/VsSection';
import WhyChooseSection, {
  ReasonItem,
} from '@/landing/components/templates/WhyChooseSection';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';
import { getAllSlugs, getLandingConfig } from '@/landing/video-translator/config';

// 动态导入 JSON 内容
const loadContent = async (jsonFileName: string): Promise<Record<string, string>> => {
  try {
    const content = await import(`@/landing/video-translator/data/${jsonFileName}`);
    return content.default || content;
  } catch (error) {
    console.error(`Failed to load content for: ${jsonFileName}`, error);
    return {};
  }
};

interface LandingPageProps {
  slug: string;
  content: Record<string, string>;
  sourceLanguage: string;
  targetLanguage: string;
}

const VideoTranslatorLandingPage: React.FC<LandingPageProps> = ({
  slug,
  content,
  sourceLanguage,
  targetLanguage,
}) => {
  // 从 JSON 获取 SEO 信息（兼容两种 key 格式）
  const pageTitle =
    content['page_title.title'] ||
    content['page_title.metaTitle'] ||
    'Video Translator | FalcoCut';
  const metaDescription =
    content['meta_description.description'] ||
    content['meta_description.metaDescription'] ||
    '';
  const ogImage =
    content['open_graph_image.imageUrl'] || 'https://falcocut.ai/images/og-img.png';
  const ogImageAlt = content['open_graph_image.altText'] || pageTitle;

  // 从 JSON 获取 Hero Section 内容
  const heroTitle = content['hero_section.h1'] || '';
  const heroSubtitle = content['hero_section.subheading'] || '';
  const heroCta = content['hero_section.cta'] || 'Upload File';

  // 从 JSON 获取 Video Translation Demos 内容
  const demosTitle = content['video_translation_demos.h2'] || '';
  const demosSubtitle = content['video_translation_demos.subheading'] || '';
  const demosCta = content['video_translation_demos.cta'] || 'Start Translating';

  // 从 JSON 获取 Features Section 内容（兼容两种 key 格式）
  const featuresTitle =
    content['features_section.h2'] || content['features_section.features.h2'] || '';
  const featuresSubtitle =
    content['features_section.subheading'] ||
    content['features_section.features.subheading'] ||
    '';
  const featuresCta =
    content['features_section.cta'] ||
    content['features_section.features.cta'] ||
    'Start Translating';

  // 功能图片列表
  const featureImages = [
    'https://assets.falcocut.ai/tools/video-translator/translate-into-different-languages-instantly.webp',
    'https://assets.falcocut.ai/tools/video-translator/extensive-library-of-authentic-ai-voices.webp',
    'https://assets.falcocut.ai/tools/video-translator/ai-voice-cloning-and-natural-dubbing.webp',
    'https://assets.falcocut.ai/tools/video-translator/accurate-subtitles-with-editing-options.webp',
    'https://assets.falcocut.ai/tools/video-translator/seamless-lip-sync-for-realism.webp',
    'https://assets.falcocut.ai/tools/video-translator/all-in-one-video-localization-platform.webp',
  ];

  // 构建功能列表（共6个功能，兼容两种 key 格式）
  const features: FeatureItem[] = [];
  for (let i = 1; i <= 6; i++) {
    const featureTitle =
      content[`features_section.feature${i}_title`] ||
      content[`features_section.features.feature${i}_title`];
    const featureText =
      content[`features_section.feature${i}_text`] ||
      content[`features_section.features.feature${i}_text`];
    if (featureTitle && featureText) {
      features.push({
        title: featureTitle,
        description: featureText,
        imageUrl: featureImages[i - 1],
        imageAlt: featureTitle,
      });
    }
  }

  // 从 JSON 获取 How To Section 内容
  const howToTitle = content['how_to_section_4_steps.h2'] || '';
  const howToCta = content['how_to_section_4_steps.cta'] || 'Start Translating';

  // 构建步骤列表（共4个步骤）
  const howToSteps: StepData[] = [];
  for (let i = 1; i <= 4; i++) {
    const stepTitle = content[`how_to_section_4_steps.step${i}_title`];
    const stepText = content[`how_to_section_4_steps.step${i}_text`];
    if (stepTitle && stepText) {
      howToSteps.push({
        number: i,
        title: stepTitle,
        description: stepText,
      });
    }
  }

  // 从 JSON 获取 Use Cases Section 内容（兼容两种 key 格式）
  const useCasesTitle =
    content['use_cases_section.useCases.h2'] || content['use_cases_section.h2'] || '';

  // 构建用例列表（共6个用例，兼容两种 key 格式）
  const useCases: UseCaseItem[] = [];
  for (let i = 1; i <= 6; i++) {
    const caseTitle =
      content[`use_cases_section.useCases.useCase${i}_title`] ||
      content[`use_cases_section.useCase${i}_title`];
    const caseText =
      content[`use_cases_section.useCases.useCase${i}_text`] ||
      content[`use_cases_section.useCase${i}_text`];
    if (caseTitle && caseText) {
      useCases.push({
        title: caseTitle,
        description: caseText,
      });
    }
  }

  // 从 JSON 获取 Language List Section 内容
  const languageListTitle = content['language_list_section.h2'] || '';
  const languageListSubtitle = content['language_list_section.subheading'] || '';
  const languageListCta = content['language_list_section.cta'] || 'Start Translating';

  // 从 JSON 获取 Why Choose Section 内容
  const whyChooseTitle = content['why_choose_falcocut.h2'] || '';
  const whyChooseSubtitle = content['why_choose_falcocut.subheading'] || '';
  const whyChooseCta = content['why_choose_falcocut.cta'] || 'Start Translating';

  // 构建理由列表（共6个理由）
  const whyChooseReasons: ReasonItem[] = [];
  for (let i = 1; i <= 6; i++) {
    const reasonTitle = content[`why_choose_falcocut.reason${i}_title`];
    const reasonText = content[`why_choose_falcocut.reason${i}_text`];
    if (reasonTitle && reasonText) {
      whyChooseReasons.push({
        title: reasonTitle,
        description: reasonText,
      });
    }
  }

  // 从 JSON 获取 VS Section 内容
  const vsTitle = content['vs_section.h2'] || '';
  const vsSubtitle = content['vs_section.subheading'] || '';
  const vsOldTitle = content['vs_section.old_title'] || 'Old Way';
  const vsNewTitle = content['vs_section.new_title'] || 'New Way with FalcoCut';
  const vsOldConclusion = content['vs_section.old_conclusion'] || '';
  const vsNewConclusion = content['vs_section.new_conclusion'] || '';
  const vsCta = content['vs_section.cta'] || 'Start Translating';

  // 构建 VS Section 要点列表
  const vsOldPoints: string[] = [];
  const vsNewPoints: string[] = [];
  for (let i = 1; i <= 5; i++) {
    const oldPoint = content[`vs_section.old_point${i}`];
    const newPoint = content[`vs_section.new_point${i}`];
    if (oldPoint) vsOldPoints.push(oldPoint);
    if (newPoint) vsNewPoints.push(newPoint);
  }

  // 从 JSON 获取 FAQ Section 内容
  const faqTitle = content['faq_section.faq.h2'] || '';

  // 构建 FAQ 列表（共10个问答）
  const faqs: FAQItem[] = [];
  for (let i = 1; i <= 10; i++) {
    const question = content[`faq_section.faq.q${i}`];
    const answer = content[`faq_section.faq.a${i}`];
    if (question && answer) {
      faqs.push({
        question,
        answer,
      });
    }
  }

  // 从 JSON 获取 Footer CTA Section 内容
  const footerCtaTitle = content['footer_cta_section.h2'] || '';
  const footerCtaSubtitle = content['footer_cta_section.subheading'] || '';
  const footerCtaCta = content['footer_cta_section.cta'] || 'Start Translating';

  // 使用共享的语言配置获取语言名称
  const sourceLanguageName = getLanguageNameByCode(sourceLanguage);
  const targetLanguageName = getLanguageNameByCode(targetLanguage);

  const currentUrl = `https://falcocut.ai/tools/video-translator/${slug}`;

  // 结构化数据
  const structuredData = createSoftwareAppSchema({
    name: `FalcoCut Video Translator - ${sourceLanguageName} to ${targetLanguageName}`,
    description: metaDescription,
    url: currentUrl,
    ratingValue: '4.9',
    ratingCount: '2350',
  });

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={metaDescription}
        url={currentUrl}
        image={ogImage}
        imageAlt={ogImageAlt}
        publishedTime="2026-01-08"
        modifiedTime="2026-01-16"
        structuredData={structuredData}
      />

      <HeroSection
        title={heroTitle}
        subtitle={heroSubtitle}
        ctaText={heroCta}
        sourceLanguageLabel="Source Language"
        sourceLanguageDefault={sourceLanguageName}
        targetLanguageLabel="Target Language"
        targetLanguageDefault={targetLanguageName}
      />

      <VideoTranslationDemos
        title={demosTitle}
        subtitle={demosSubtitle}
        ctaText={demosCta}
      />

      {features.length > 0 && (
        <FeaturesSection
          title={featuresTitle}
          subtitle={featuresSubtitle}
          features={features}
          ctaText={featuresCta}
        />
      )}

      {howToSteps.length > 0 && (
        <HowToSection title={howToTitle} steps={howToSteps} ctaText={howToCta} />
      )}

      {useCases.length > 0 && (
        <UseCasesSection title={useCasesTitle} useCases={useCases} />
      )}

      {languageListTitle && (
        <LanguageListSection
          title={languageListTitle}
          subtitle={languageListSubtitle}
          ctaText={languageListCta}
        />
      )}

      {whyChooseReasons.length > 0 && (
        <WhyChooseSection
          title={whyChooseTitle}
          subtitle={whyChooseSubtitle}
          reasons={whyChooseReasons}
          ctaText={whyChooseCta}
        />
      )}

      {vsOldPoints.length > 0 && vsNewPoints.length > 0 && (
        <VsSection
          title={vsTitle}
          subtitle={vsSubtitle}
          oldTitle={vsOldTitle}
          oldPoints={vsOldPoints}
          oldConclusion={vsOldConclusion}
          newTitle={vsNewTitle}
          newPoints={vsNewPoints}
          newConclusion={vsNewConclusion}
          ctaText={vsCta}
        />
      )}

      <ExploreToolsSection />

      {faqs.length > 0 && <FAQSection title={faqTitle} faqs={faqs} />}

      {footerCtaTitle && (
        <FooterCTASection
          title={footerCtaTitle}
          subtitle={footerCtaSubtitle}
          ctaText={footerCtaCta}
        />
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = getAllSlugs();

  return {
    paths: slugs.map((slug) => ({
      params: { slug },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<LandingPageProps> = async ({ params }) => {
  const slug = params?.slug as string;
  const config = getLandingConfig(slug);

  if (!config) {
    return {
      notFound: true,
    };
  }

  const content = await loadContent(config.jsonFileName);

  return {
    props: {
      slug,
      content,
      sourceLanguage: config.sourceLanguage,
      targetLanguage: config.targetLanguage,
    },
  };
};

export default withLandingLayout(VideoTranslatorLandingPage);
